/**
 * OneDrive Service
 * Frontend API client for OneDrive integration
 */

const API_BASE = 'http://localhost:3001/api/onedrive';

/**
 * Check if OneDrive is connected for the current brand
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<Object>} Connection status with details
 */
export async function checkOneDriveConnection(userId, brandId) {
  if (!userId || !brandId) {
    throw new Error('userId and brandId are required');
  }

  const response = await fetch(
    `${API_BASE}/connection-status?userId=${encodeURIComponent(userId)}&brandId=${encodeURIComponent(brandId)}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check connection status');
  }

  return response.json();
}

/**
 * Initiate OneDrive OAuth connection
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @returns {Promise<Object>} OAuth auth URL and state
 */
export async function initiateOneDriveConnection(userId, brandId) {
  if (!userId || !brandId) {
    throw new Error('userId and brandId are required');
  }

  const response = await fetch(`${API_BASE}/connect`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ userId, brandId })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to initiate connection');
  }

  return response.json();
}

/**
 * Disconnect OneDrive
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {string} connectionId - The connection ID
 * @returns {Promise<Object>} Disconnect result
 */
export async function disconnectOneDrive(userId, brandId, connectionId) {
  if (!userId || !connectionId) {
    throw new Error('userId and connectionId are required');
  }

  const response = await fetch(`${API_BASE}/disconnect`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ userId, brandId, connectionId })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to disconnect');
  }

  return response.json();
}

/**
 * List files from OneDrive
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {string} folderId - The folder ID (optional, defaults to root)
 * @returns {Promise<Object>} List of files and folders
 */
export async function listOneDriveFiles(userId, brandId, folderId = null) {
  if (!userId || !brandId) {
    throw new Error('userId and brandId are required');
  }

  let url = `${API_BASE}/files?userId=${encodeURIComponent(userId)}&brandId=${encodeURIComponent(brandId)}`;
  if (folderId) {
    url += `&folderId=${encodeURIComponent(folderId)}`;
  }

  const response = await fetch(url);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to list files');
  }

  return response.json();
}

/**
 * Get thumbnail URL for a OneDrive file
 * @param {string} userId - The user ID
 * @param {string} brandId - The brand ID
 * @param {string} fileId - The file ID
 * @param {string} size - Thumbnail size (small, medium, large)
 * @returns {string} Thumbnail URL
 */
export function getOneDriveThumbnailUrl(userId, brandId, fileId, size = 'medium') {
  return `${API_BASE}/thumbnail?userId=${encodeURIComponent(userId)}&brandId=${encodeURIComponent(brandId)}&fileId=${encodeURIComponent(fileId)}&size=${encodeURIComponent(size)}`;
}

/**
 * Format file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size string
 */
export function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
